import { useMemo, useState, useEffect } from 'react'
import { Plus, Search } from 'lucide-react'

import type { Deal, DealStage } from '@/types/deal'
import { useDeals, useUpdateDeal } from '@/features/customers/deal-hooks'
import { openDeals, sumValue } from '@/features/customers/deal-utils'
import { formatCurrency } from '@/lib/format'
import { useDebounce } from '@/components/hooks/use-debounce'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { KanbanBoard } from './kanban-board'
import { DealEditDialog } from './deal-edit-dialog'
import { ComposeMessageDialog } from '@/features/messaging/components/compose-message-dialog'

interface DealsTabProps {
  q?: string
  onQChange?: (q: string) => void
}

export function DealsTab({ q: urlQ, onQChange }: DealsTabProps) {
  const [searchInput, setSearchInput] = useState(urlQ ?? '')
  const debouncedQ = useDebounce(searchInput, 300)

  useEffect(() => {
    if (onQChange && debouncedQ !== (urlQ ?? '')) {
      onQChange(debouncedQ)
    }
  }, [debouncedQ, urlQ, onQChange])

  const { data: fetchedDeals = [], isLoading, isError } = useDeals(debouncedQ || undefined)
  const updateMutation = useUpdateDeal()

  const [deals, setDeals] = useState<Deal[]>(fetchedDeals)

  // Mirror the latest server data into local state so the kanban can apply
  // optimistic stage updates. "Adjust state during render" (React docs)
  // avoids a setState-in-effect cascading render.
  const [prevFetched, setPrevFetched] = useState(fetchedDeals)
  if (prevFetched !== fetchedDeals) {
    setPrevFetched(fetchedDeals)
    setDeals(fetchedDeals)
  }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Deal | null>(null)
  const [messagingDeal, setMessagingDeal] = useState<Deal | null>(null)

  const pipelineTotal = useMemo(() => sumValue(openDeals(deals)), [deals])
  const openCount = useMemo(() => openDeals(deals).length, [deals])

  const handleStageChange = (dealId: string, newStage: DealStage) => {
    setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage: newStage } : d)))

    const target = deals.find((d) => d.id === dealId)
    if (!target) return
    updateMutation.mutate({
      id: dealId,
      body: {
        title: target.title,
        base_value: target.base_value,
        stage: newStage,
        owner: target.owner,
        customer_id: target.customer_id,
        // The PUT is a full replace — omitting boat_id would clear the
        // deal↔boat link server-side, so always re-send the current link.
        boat_id: target.boat_id ?? undefined,
        expected_close: target.expected_close,
      },
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="secondary">{openCount} open</Badge>
          <span className="text-sm text-muted-foreground">
            Pipeline value{' '}
            <span className="font-semibold text-foreground">{formatCurrency(pipelineTotal)}</span>
          </span>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search deals…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-8 sm:w-60"
            />
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Deal
          </Button>
        </div>
      </div>

      {isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load deals. Is the backend running?
        </div>
      )}

      {isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex min-w-60 flex-1 flex-col gap-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <KanbanBoard
          deals={deals}
          onStageChange={handleStageChange}
          onCardEdit={(d) => setEditing(d)}
          onCardMessage={(d) => setMessagingDeal(d)}
        />
      )}

      <DealEditDialog open={createOpen} onOpenChange={setCreateOpen} mode="create" />
      <DealEditDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        mode="edit"
        deal={editing}
      />
      <ComposeMessageDialog
        open={!!messagingDeal}
        onOpenChange={(o) => !o && setMessagingDeal(null)}
        dealId={messagingDeal?.id}
      />
    </div>
  )
}
